
import React, { useState, useRef, useEffect } from 'react';
import { SectionWrapper } from './SectionWrapper';
import { RefreshCw, Wind, Smile, CloudRain, Flame, Eye, Zap, Frown, EyeOff, Scissors, Waves } from 'lucide-react';

type EmotionKey = 'joy' | 'sadness' | 'anger' | 'fear' | 'surprise' | 'disgust' | 'shame' | 'envy';

interface EmotionData {
  id: EmotionKey;
  label: string;
  icon: React.ReactNode;
  color: string;
  bubbleColor: string;
  textColor: string;
  subEmotions: string[];
  guidance: string;
}

const EMOTIONS: Record<EmotionKey, EmotionData> = {
  joy: {
    id: 'joy',
    label: '快乐',
    icon: <Smile strokeWidth={1.2} />,
    color: 'bg-amber-50',
    bubbleColor: 'bg-amber-100',
    textColor: 'text-amber-800', 
    subEmotions: ['欢欣', '愉悦', '畅快', '满足', '幸福', '陶醉', '自豪', '轻松', '振奋', '豁然开朗', '宁静', '安详'],
    guidance: '感受这份光亮在身体里流动。不需要抓住它，只需允许它存在，成为你力量的源泉。'
  },
  sadness: {
    id: 'sadness',
    label: '悲伤',
    icon: <CloudRain strokeWidth={1.2} />,
    color: 'bg-slate-50',
    bubbleColor: 'bg-slate-200',
    textColor: 'text-slate-700',
    subEmotions: ['忧郁', '失落', '沮丧', '绝望', '孤独', '空虚', '消沉', '痛心', '遗憾', '惆怅', '心碎', '无助', '凄凉', '悲愤', '委屈', '麻木'],
    guidance: '悲伤是灵魂在降雨。在这里，你不需要“好起来”。允许自己淋一会儿雨，我会在这里陪着你，直到雨停。'
  },
  anger: {
    id: 'anger',
    label: '愤怒',
    icon: <Flame strokeWidth={1.2} />,
    color: 'bg-rose-50',
    bubbleColor: 'bg-rose-100',
    textColor: 'text-rose-800',
    subEmotions: ['恼火', '暴怒', '愤恨', '烦躁', '怨恨', '气恼', '不平', '焦躁', '妒火', '不满', '激愤', '压抑'],
    guidance: '愤怒是生命力受阻时的呐喊，它在保护那个受伤的自我。试着深呼吸，感受火焰的温度，但不要让它灼伤你自己。'
  },
  fear: {
    id: 'fear',
    label: '恐惧',
    icon: <Eye strokeWidth={1.2} />,
    color: 'bg-indigo-50/50',
    bubbleColor: 'bg-indigo-100',
    textColor: 'text-indigo-800',
    subEmotions: ['胆怯', '惊恐', '焦虑', '畏惧', '恐慌', '不安', '紧张', '慌乱', '忧虑', '心悸', '忐忑', '迷茫', '失控'],
    guidance: '恐惧是因为我们在意。你的身体在试图保护你。把手放在胸口，告诉它：“我现在是安全的。”'
  },
  shame: {
    id: 'shame',
    label: '羞耻',
    icon: <EyeOff strokeWidth={1.2} />,
    color: 'bg-purple-50',
    bubbleColor: 'bg-purple-100',
    textColor: 'text-purple-900',
    subEmotions: ['羞愧', '内疚', '自责', '难堪', '无地自容', '耻辱', '卑微', '渺小', '不配', '尴尬', '自我厌恶'],
    guidance: '羞耻感像是把自己包裹在洋葱里，不想被任何人发现。但在这里，没有审判。你不必完美，你值得被接纳。'
  },
  surprise: {
    id: 'surprise',
    label: '惊讶',
    icon: <Zap strokeWidth={1.2} />,
    color: 'bg-teal-50',
    bubbleColor: 'bg-teal-100',
    textColor: 'text-teal-800',
    subEmotions: ['震惊', '愕然', '诧异', '错愕', '意外', '惊奇', '目瞪口呆', '匪夷所思', '出乎意料'],
    guidance: '变化总是突然降临。在这一刻的失衡中，尝试去觉察新的可能性。未知不仅仅是危险，也包含着生机。'
  },
  disgust: {
    id: 'disgust',
    label: '厌恶',
    icon: <Frown strokeWidth={1.2} />,
    color: 'bg-lime-50',
    bubbleColor: 'bg-lime-100',
    textColor: 'text-lime-800',
    subEmotions: ['恶心', '反感', '嫌弃', '厌烦', '作呕', '抵触', '鄙夷', '不屑', '难以忍受'],
    guidance: '厌恶是心理边界的卫士，它在告诉你“有些东西不适合我”。尊重你的排斥感，这是你在确立自己的边界。'
  },
  envy: {
    id: 'envy',
    label: '嫉妒',
    icon: <Scissors strokeWidth={1.2} className="rotate-90" />,
    color: 'bg-emerald-50',
    bubbleColor: 'bg-emerald-100',
    textColor: 'text-emerald-800',
    subEmotions: ['忌妒', '眼红', '不甘心', '攀比', '酸楚', '掠夺欲', '被剥夺感', '刻薄'],
    guidance: '嫉妒是因为我们看见了美好，却痛苦于它不在自己手中。试着承认这份渴望，它背后藏着你真正想要成为的样子。'
  }
};

type Stage = 'select-primary' | 'select-secondary' | 'resonance';

interface VanishingInputProps { 
  onAbsorb: () => void;
  onActiveChange: (isActive: boolean) => void;
}

const VanishingInput: React.FC<VanishingInputProps> = ({ onAbsorb, onActiveChange }) => {
  const [thought, setThought] = useState('');
  const [activeThoughts, setActiveThoughts] = useState<{id: number, text: string}[]>([]);
  const idCounter = useRef(0);

  useEffect(() => {
    onActiveChange(activeThoughts.length > 0);
  }, [activeThoughts, onActiveChange]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!thought.trim()) return;
    
    const newId = idCounter.current++;
    setActiveThoughts(prev => [...prev, { id: newId, text: thought }]);
    setThought('');
    
    // 触发外部吸入动画
    setTimeout(() => onAbsorb(), 2100);
    
    // 清理当前显示的念头
    setTimeout(() => {
      setActiveThoughts(prev => prev.filter(t => t.id !== newId));
    }, 6000);
  };

  return (
    <div className="mt-12 md:mt-20 w-full max-w-md relative min-h-[160px] flex flex-col items-center z-20 px-4">
      {/* 移除外部 Label，保持界面极简 */}

      <form onSubmit={handleSubmit} className="w-full flex flex-col items-center relative group">
        <input
          type="text"
          value={thought}
          onChange={(e) => setThought(e.target.value)}
          placeholder="让无声的感受，落成文字……"
          className="w-full bg-transparent border-b-2 border-stone-200 focus:border-stone-400 py-3 text-center text-xl md:text-2xl font-serif text-stone-800 placeholder:text-stone-300 placeholder:italic focus:outline-none transition-all font-bold"
        />
        
        {/* 实体按钮：信号发送 */}
        <div className={`
            mt-8 transition-all duration-500 transform
            ${thought.trim() ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}
        `}>
          <button 
            type="submit"
            className="bg-stone-600 text-[#fcfbf9] px-12 py-3.5 rounded-full shadow-[0_4px_14px_rgba(0,0,0,0.1)] hover:shadow-[0_6px_20px_rgba(0,0,0,0.15)] hover:bg-stone-500 hover:scale-105 active:scale-95 transition-all duration-500"
          >
            <span className="font-serif tracking-[0.3em] text-sm font-bold">体会念头</span>
          </button>
        </div>
      </form>
      
      <div className="absolute top-12 left-0 right-0 pointer-events-none">
        {activeThoughts.map((t) => (
          <div key={t.id} className="absolute w-full text-center animate-rise-center font-serif text-xl md:text-2xl text-stone-900 font-black italic">
            {t.text}
          </div>
        ))}
      </div>
    </div>
  );
};

export const MoodSpace: React.FC = () => {
  const [stage, setStage] = useState<Stage>('select-primary');
  const [selectedPrimary, setSelectedPrimary] = useState<EmotionKey | null>(null);
  const [selectedSecondary, setSelectedSecondary] = useState<string | null>(null);
  const [isAbsorbing, setIsAbsorbing] = useState(false);
  const [hasFloatingThoughts, setHasFloatingThoughts] = useState(false);

  const handlePrimarySelect = (key: EmotionKey) => {
    setSelectedPrimary(key);
    setStage('select-secondary');
  };

  const handleSecondarySelect = (sub: string) => {
    setSelectedSecondary(sub);
    setStage('resonance');
  };

  const reset = () => {
    setStage('select-primary');
    setSelectedPrimary(null);
    setSelectedSecondary(null);
    setIsAbsorbing(false);
    setHasFloatingThoughts(false);
  };

  const triggerAbsorption = () => {
    setIsAbsorbing(true);
    setTimeout(() => setIsAbsorbing(false), 3500);
  };

  const currentEmotion = selectedPrimary ? EMOTIONS[selectedPrimary] : null;
  const splitIndex = selectedSecondary ? Math.ceil(selectedSecondary.length / 2) : 0;
  const leftPart = selectedSecondary ? selectedSecondary.slice(0, splitIndex) : '';
  const rightPart = selectedSecondary ? selectedSecondary.slice(splitIndex) : '';

  return (
    <SectionWrapper noDecoration className={`min-h-[70vh] transition-colors duration-[2500ms] ease-in-out ${stage === 'resonance' && currentEmotion ? currentEmotion.color : ''}`}>
      {/* 统一头部：情绪 */}
      <div className="text-center mb-10 md:mb-12 animate-fade-in flex flex-col items-center">
        <h2 className="font-serif text-3xl md:text-5xl text-stone-900 tracking-widest mb-4 font-black">
          情绪空间
        </h2>
        <div className="flex flex-col items-center gap-2">
          <span className="text-stone-400 font-sans uppercase text-xs tracking-[0.6em] font-bold">The Mental Landscape</span>
          <p className="text-stone-600 font-serif text-xl mt-10 max-w-xl leading-loose italic">
            {stage === 'select-primary' && "“此时此刻与你的情绪呆在一起。”"}
            {stage === 'select-secondary' && "“试着为这份感受，找一个更准确的名字。”"}
            {stage === 'resonance' && "“此刻，请试着感受双脚踩在地面的重量。”"}
          </p>
        </div>
      </div>

      <div className="relative min-h-[450px] flex items-center justify-center">
        {stage === 'select-primary' && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-x-12 md:gap-y-10 w-full max-w-3xl animate-fade-in pb-10 justify-items-center">
            {Object.values(EMOTIONS).map((emotion, index) => (
              <button key={emotion.id} onClick={() => handlePrimarySelect(emotion.id)} className={`relative group w-24 h-24 md:w-28 md:h-28 rounded-full flex items-center justify-center ${emotion.bubbleColor} ${emotion.textColor} shadow-sm transition-all duration-500 hover:scale-110 hover:shadow-md animate-float-random-${index % 3} cursor-pointer`}>
                <span className="font-serif text-lg md:text-xl font-black tracking-widest relative z-10">{emotion.label}</span>
              </button>
            ))}
          </div>
        )}

        {stage === 'select-secondary' && currentEmotion && (
          <div className="flex flex-col items-center animate-fade-in w-full">
            <div className={`w-32 h-32 rounded-full flex items-center justify-center mb-14 ${currentEmotion.bubbleColor} ${currentEmotion.textColor} shadow-inner`}>
              <div className="[&>svg]:w-16 [&>svg]:h-16">{currentEmotion.icon}</div>
            </div>
            <div className="flex flex-wrap justify-center gap-x-5 gap-y-5 max-w-3xl px-4">
              {currentEmotion.subEmotions.map((sub, index) => (
                <button key={sub} onClick={() => handleSecondarySelect(sub)} className="px-5 py-2 rounded-full border border-stone-200 bg-white/60 text-stone-900 font-serif text-lg font-bold hover:border-stone-500 hover:scale-105 transition-all duration-300">
                  {sub}
                </button>
              ))}
            </div>
            <button onClick={reset} className="mt-20 text-xs text-stone-500 font-black hover:text-stone-900 border-b border-stone-300 transition-all uppercase tracking-widest">重新选择</button>
          </div>
        )}

        {stage === 'resonance' && currentEmotion && selectedSecondary && (
          <div className="flex flex-col items-center justify-center text-center animate-fade-in w-full max-w-xl">
            <div className="relative mb-8 mt-4">
               {isAbsorbing && <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 rounded-full border-2 border-stone-400/30 opacity-0 animate-ripple-out"></div>}
               <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full ${currentEmotion.bubbleColor} transition-all duration-[2500ms] ${isAbsorbing ? 'w-96 h-96 opacity-90 blur-md saturate-150 brightness-90' : 'w-72 h-72 opacity-20 animate-deep-breath'}`}></div>
               <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full ${currentEmotion.bubbleColor} transition-all duration-[2500ms] ${isAbsorbing ? 'w-80 h-80 opacity-100 blur-sm saturate-200 brightness-75' : 'w-56 h-56 opacity-30 animate-deep-breath'}`}></div>
               <div className={`relative z-10 font-serif text-5xl md:text-6xl ${currentEmotion.textColor} font-black tracking-[0.2em] flex justify-center gap-1 transition-all duration-[2000ms] ${isAbsorbing ? 'scale-110 saturate-[2.5] brightness-75' : 'scale-100'}`}>
                 <span className={`transition-transform duration-[1500ms] ${isAbsorbing ? '-translate-x-8' : ''}`}>{leftPart}</span>
                 <span className={`transition-transform duration-[1500ms] ${isAbsorbing ? 'translate-x-8' : ''}`}>{rightPart}</span>
               </div>
            </div>
            <div className={`space-y-8 relative z-10 transition-all duration-[1000ms] ${hasFloatingThoughts ? 'opacity-10 blur-sm grayscale' : 'opacity-100'}`}>
              <div className="w-10 h-[1px] bg-stone-300 mx-auto opacity-50"></div>
              <p className="font-serif text-xl text-stone-900 leading-relaxed italic px-8 h-20 flex items-center justify-center">“{currentEmotion.guidance}”</p>
              <div className="w-10 h-[1px] bg-stone-300 mx-auto opacity-50"></div>
            </div>
            <VanishingInput onAbsorb={triggerAbsorption} onActiveChange={setHasFloatingThoughts} />
            <button onClick={reset} className="mt-16 flex items-center gap-2 text-stone-500 font-black hover:text-stone-900 z-20 uppercase tracking-widest"><RefreshCw size={14} /><span>重新探索</span></button>
          </div>
        )}
      </div>
      <div className="mt-8 text-center opacity-40"><Waves className="mx-auto text-stone-300 animate-pulse" /></div>
    </SectionWrapper>
  );
};
